import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { CONTACT } from "@/data/site";

const NAV = [
  { to: "/", key: "home" },
  { to: "/sobe", key: "rooms" },
  { to: "/lokacija", key: "location" },
  { to: "/gallery", key: "gallery" },
  { to: "/dining", key: "dining" },
  { to: "/medjugorje", key: "medjugorje" },
  { to: "/kontakt", key: "contact" },
];

export function Footer() {
  const { t, setLang } = useLang();
  const year = new Date().getFullYear();

  return (
    <footer data-testid="site-footer" className="bg-[#1F2421] text-[#F9F8F3]/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-14 sm:py-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
        <div>
          <p className="font-serif-display text-3xl text-[#F9F8F3] mb-1">Sobe Bevanda</p>
          <p className="text-[11px] uppercase tracking-[0.22em] text-[#D8CFBC]/70 mb-5">Međugorje</p>
          <p className="text-sm leading-relaxed text-[#F9F8F3]/60 max-w-xs">{t.footer.tagline}</p>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#D8CFBC]/70 mb-5">{t.footer.nav}</p>
          <ul className="space-y-2.5">
            {NAV.map((item) => (
              <li key={item.key}>
                <Link
                  to={item.to}
                  data-testid={`footer-nav-${item.key}`}
                  className="text-sm hover:text-[#F9F8F3] transition-colors"
                >
                  {t.nav[item.key]}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#D8CFBC]/70 mb-5">{t.footer.contact}</p>
          <ul className="space-y-3 text-sm">
            <li className="flex items-start gap-2.5">
              <MapPin size={15} className="mt-0.5 shrink-0 text-[#D8CFBC]/70" />
              <span>{CONTACT.address}</span>
            </li>
            <li>
              <a href={CONTACT.phoneHref} data-testid="footer-phone" className="flex items-center gap-2.5 hover:text-[#F9F8F3] transition-colors">
                <Phone size={15} className="shrink-0 text-[#D8CFBC]/70" /> {CONTACT.phoneDisplay}
              </a>
            </li>
            <li>
              <a
                href={`https://wa.me/${CONTACT.whatsapp}?text=${encodeURIComponent(t.contactPage.waMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-whatsapp"
                className="flex items-center gap-2.5 hover:text-[#F9F8F3] transition-colors"
              >
                <MessageCircle size={15} className="shrink-0 text-[#D8CFBC]/70" /> WhatsApp
              </a>
            </li>
            <li>
              <a href={`mailto:${CONTACT.email}`} data-testid="footer-email" className="flex items-center gap-2.5 hover:text-[#F9F8F3] transition-colors">
                <Mail size={15} className="shrink-0 text-[#D8CFBC]/70" /> {CONTACT.email}
              </a>
            </li>
          </ul>
          <p className="text-xs uppercase tracking-[0.22em] text-[#D8CFBC]/70 mt-8 mb-3">{t.footer.languages}</p>
          <div className="flex gap-2">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                data-testid={`footer-lang-${l.code}`}
                onClick={() => setLang(l.code)}
                className="text-xs font-semibold tracking-wide border border-[#F9F8F3]/20 rounded-full px-3 py-1.5 hover:bg-[#F9F8F3]/10 transition-colors"
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.22em] text-[#D8CFBC]/70 mb-5">{t.footer.map}</p>
          <div className="overflow-hidden rounded-lg border border-[#F9F8F3]/10">
            <iframe
              title="Sobe Bevanda — map"
              src={CONTACT.mapEmbed}
              className="w-full h-44 grayscale-[30%] opacity-90"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>

      <div className="border-t border-[#F9F8F3]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-[#F9F8F3]/50">
          <p>© {year} Sobe Bevanda, Međugorje. {t.footer.rights}</p>
          <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#F9F8F3] transition-colors">
            {t.footer.privacy}
          </Link>
        </div>
      </div>
    </footer>
  );
}
